import { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { ChevronLeft, PlayCircle } from 'lucide-react';
import { Box, Tooltip } from '@mui/material';
import { Typography, Button, Paper } from '@mui/material';
import { APP_TOUR_CHROME_ATTR, APP_TOUR_POPPER_CLASS } from 'Constants/AppTourMarkup';
import { ApptourFeature, ApptourFeatureItem } from 'Utils/AppTourFeatures';

// eslint-disable-next-line react-refresh/only-export-components -- enum shared with consumers
export enum TooltipRelativePosition {
  LEFT = 'left',
  TOP = 'top',
  RIGHT = 'right',
  BOTTOM = 'bottom',
}

export interface AppTourStep {
  targetId: string;
  positionTarget?: string;
  title: string;
  content: React.ReactNode; // changed from string to React.ReactNode
  position: TooltipRelativePosition | 'left' | 'right' | 'top' | 'bottom';
  positionOffset?: [number, number];
  preStepFunction?: () => void;
  postStepFunction?: () => void;
  onSkipTour?: () => void;
  onClose?: () => void;
  feature?: ApptourFeature;
  newFeature?: boolean;
}

// Builds the new-feature walkthrough by walking the backend-provided unseen
// list (already ordered by feature.id ASC) and picking the matching step for
// each slug. Features without a corresponding step in this tour are skipped —
// the unseen list is global across tours, so each tour only shows what it has
// content for. Steps are tagged `newFeature: true` so the tooltip shows the
// NEW! badge and the final button reads GREAT instead of FINISH.
export function filterByUnseenFeatures(
  steps: AppTourStep[],
  unseenFeatures: ApptourFeatureItem[],
): AppTourStep[] {
  return unseenFeatures
    .map((f) => steps.find((s) => s.feature === f.key))
    .filter((step): step is AppTourStep => step !== undefined)
    .map((step) => ({ ...step, newFeature: true }));
}

const bgColor = 'rgb(5, 11, 19)';

/** MUI Switch puts `id` on a hidden checkbox; glow on that node is invisible — use the visible track/thumb root. */
function getTourHighlightElement(el: HTMLElement): HTMLElement {
  if (el.tagName === 'INPUT' && (el as HTMLInputElement).type === 'checkbox') {
    const root = el.closest('.MuiSwitch-root');
    if (root instanceof HTMLElement) {
      return root;
    }
  }
  return el;
}

function isConnectedElement(el: HTMLElement | null): el is HTMLElement {
  return !!el && el.isConnected;
}

export interface AppTourProps {
  steps: AppTourStep[];
  isOpen: boolean;
  onClose?: () => void;
  onStepChange?: (step: number) => void;
  preTourSetup?: () => void;
  onSkipTour?: () => void;
  videoLink?: string;
  skipIntro?: boolean;
  tooltipClassName?: string;
}

export default function AppTour({
  steps,
  isOpen,
  onClose,
  onStepChange,
  preTourSetup,
  onSkipTour,
  videoLink,
  tooltipClassName = 'tour-tooltip',
  skipIntro = false,
}: AppTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [showIntro, setShowIntro] = useState(!skipIntro);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const elevatedElsRef = useRef<Array<{ el: HTMLElement; zIndex: string; position: string }>>([]);
  const lastTargetRef = useRef<HTMLElement | null>(null);
  const lastTargetBoxShadowRef = useRef<string>('');
  // Pending step transition: while we wait (via rAF) for the next step's anchor
  // to mount, navRafRef holds the scheduled frame and pendingStepRef the step
  // we're moving to (used to ignore re-entrant nav clicks).
  const navRafRef = useRef<number | null>(null);
  const pendingStepRef = useRef<number | null>(null);
  const currentStepObj = steps[currentStep];
  const currentPosition = currentStepObj.position;
  // Stable Popper modifiers. Every click dispatches setInteractedWithPage (see
  // App.tsx), which re-renders this tree; if the modifiers/offset arrays were
  // re-allocated each render, MUI's Popper would re-run its position calc and
  // nudge the tooltip a few px ("tiny jump"). Memoizing on the primitive offset
  // values keeps the reference stable so unrelated re-renders don't reposition.
  const offsetX = currentStepObj.positionOffset?.[0] ?? 0;
  const offsetY = currentStepObj.positionOffset?.[1] ?? 0;
  const popperModifiers = useMemo(
    () => [{ name: 'offset', options: { offset: [offsetX, offsetY] } }],
    [offsetX, offsetY],
  );
  const prevStepRef = useRef<number>(currentStep);

  useEffect(() => {
    if (skipIntro) {
      setShowIntro(false);
    }
  }, [skipIntro]);

  const targetId = isOpen && !showIntro && currentStepObj ? currentStepObj.targetId : null;
  const positionTargetId =
    isOpen && !showIntro && currentStepObj?.positionTarget ? currentStepObj.positionTarget : null;

  // Resolve anchors after DOM commit. useMemo + getElementById during render reads the *previous*
  // commit's DOM, so targets inside UI that mounts in the same update (e.g. expanded tempo panel)
  // are missing until a later render. useLayoutEffect runs after React applies this commit's DOM.
  const [targetElement, setTargetElement] = useState<HTMLElement | null>(null);
  const [positionTargetElement, setPositionTargetElement] = useState<HTMLElement | null>(null);
  const currentTargetElement =
    isConnectedElement(targetElement) && targetElement.id === currentStepObj.targetId
      ? targetElement
      : null;
  const currentPositionTargetElement =
    isConnectedElement(positionTargetElement) &&
    !!currentStepObj.positionTarget &&
    positionTargetElement.id === currentStepObj.positionTarget
      ? positionTargetElement
      : null;
  // The element the Popper measures against. Null while a step's target is
  // still resolving (async popups / MutationObserver path) — the render below
  // hides the popper in that window so it never flashes at the origin.
  const tooltipAnchorEl = currentPositionTargetElement ?? currentTargetElement;

  // MUI's Popper applies its computed position in an effect that runs *after*
  // the first paint, so the frame where `anchorEl` first becomes non-null is
  // painted at the popper's default origin (0,0) before it snaps to the target.
  // Gating opacity on the anchor merely existing therefore still flashes. We
  // additionally wait until the popper has had a frame to place itself before
  // revealing it: reset on every anchor change, then flip ready on the next
  // animation frame (by which point Popper's positioning effect has run and
  // painted). The ~1-frame reveal delay is imperceptible (MUS-275).
  const [anchorPositioned, setAnchorPositioned] = useState(false);
  useEffect(() => {
    if (!tooltipAnchorEl) {
      setAnchorPositioned(false);
      return;
    }
    setAnchorPositioned(false);
    let inner = 0;
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => setAnchorPositioned(true));
    });
    return () => {
      cancelAnimationFrame(outer);
      if (inner) cancelAnimationFrame(inner);
    };
  }, [tooltipAnchorEl]);

  const tooltipVisible = !!tooltipAnchorEl && anchorPositioned;

  useLayoutEffect(() => {
    if (!targetId) {
      setTargetElement(null);
      setPositionTargetElement(null);
      return;
    }

    let raf: number | undefined;
    let observer: MutationObserver | undefined;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    let cancelled = false;

    const resolve = () => {
      const el = document.getElementById(targetId);
      const posEl = positionTargetId ? document.getElementById(positionTargetId) : null;
      setTargetElement(el);
      setPositionTargetElement(posEl);
      return !!el;
    };

    // 1. Immediate attempt (handles already-mounted elements)
    if (!resolve()) {
      // 2. rAF attempt (handles same-commit mounts)
      raf = requestAnimationFrame(() => {
        if (cancelled) return;
        if (!resolve()) {
          // 3. MutationObserver fallback for async popups / portals.
          // Watches for the target being added to the DOM and resolves
          // on the first hit. Disconnected on cleanup so step changes
          // and tour close won't cause recursive triggers.
          observer = new MutationObserver(() => {
            if (cancelled) return;
            if (resolve()) {
              observer?.disconnect();
              if (timeout !== undefined) clearTimeout(timeout);
            }
          });
          observer.observe(document.body, { childList: true, subtree: true });
          timeout = setTimeout(() => {
            observer?.disconnect();
          }, 3000);
        }
      });
    }

    return () => {
      cancelled = true;
      if (raf !== undefined) cancelAnimationFrame(raf);
      observer?.disconnect();
      if (timeout !== undefined) clearTimeout(timeout);
    };
  }, [targetId, positionTargetId, currentStep]);

  // Max frames to wait for a step's anchor to mount before committing anyway.
  // Targets normally appear on frame 1; this just caps the wait so a step whose
  // target never mounts can't hang the tour (the resolve effect's
  // MutationObserver still picks it up afterwards). ~20 frames ≈ 1/3s.
  const MAX_ANCHOR_WAIT_FRAMES = 20;

  // Move to a step without the tooltip jumping. We first run the next step's
  // preStepFunction (which opens/mounts its target — e.g. a popup or dropdown),
  // then wait via requestAnimationFrame until that anchor is actually in the
  // DOM, and only then flip currentStep. Because the anchor exists when the
  // step changes, the resolve effect picks it up synchronously and the Popper
  // never points at a stale/empty anchor — so a single click advances cleanly.
  const navigateToStep = (nextStep: number) => {
    // Ignore re-entrant nav (rapid clicks / Enter) until the current
    // transition commits, so we never skip a step.
    if (pendingStepRef.current !== null) return;

    const fromStep = currentStep;
    pendingStepRef.current = nextStep;

    const next = steps[nextStep];
    // Mount/open the next step's target before moving the tooltip.
    if (next?.preStepFunction) {
      next.preStepFunction();
    }

    let frames = 0;
    const commit = () => {
      navRafRef.current = null;
      pendingStepRef.current = null;
      setCurrentStep(nextStep);
      if (onStepChange) {
        onStepChange(nextStep);
      }
      // Tear down the step we're leaving only after advancing, so there is
      // never a frame where neither the old nor the new anchor is present.
      if (steps[fromStep]?.postStepFunction) {
        steps[fromStep].postStepFunction();
      }
    };

    // Commit as soon as the next step's anchor exists. The popups these steps
    // target fade in without translating (MUS-275), so the anchor's rect is
    // stationary from mount — MUI's Popper measures it once and stays correct,
    // and we don't need to wait out an entrance animation here.
    const waitForAnchor = () => {
      const anchorReady = !next?.targetId || document.getElementById(next.targetId) !== null;
      if (anchorReady || frames >= MAX_ANCHOR_WAIT_FRAMES) {
        commit();
      } else {
        frames++;
        navRafRef.current = requestAnimationFrame(waitForAnchor);
      }
    };
    navRafRef.current = requestAnimationFrame(waitForAnchor);
  };

  // Handle navigation between steps
  const goToNextStep = () => {
    if (currentStep < steps.length - 1) {
      navigateToStep(currentStep + 1);
    } else {
      handleClose();
    }
  };

  const goToPrevStep = () => {
    if (currentStep > 0) {
      navigateToStep(currentStep - 1);
    }
  };

  // Cancel any pending step-transition frame on unmount.
  useEffect(() => {
    return () => {
      if (navRafRef.current !== null) {
        cancelAnimationFrame(navRafRef.current);
        navRafRef.current = null;
      }
    };
  }, []);

  // Initialize first step only when intro is dismissed. Navigation already runs
  // pre-step for subsequent steps to guarantee deterministic ordering.
  useEffect(() => {
    if (isOpen && !showIntro && currentStep === 0 && currentStepObj?.preStepFunction) {
      currentStepObj.preStepFunction();
    }
  }, [isOpen, showIntro, currentStep, currentStepObj]);

  // Handle keyboard navigation (duplicate listener removed below — single handler after intro).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen || showIntro) return;

      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        goToNextStep();
      } else if (e.key === 'ArrowLeft') {
        goToPrevStep();
      } else if (e.key === 'Escape') {
        handleClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep, showIntro]);

  // Apply styles to highlight and elevate the target element (and any stacking-context ancestors).
  // useLayoutEffect runs synchronously after DOM mutations but before the browser paints, so the
  // Popper positions itself against an already-elevated anchor on the very first render of each step.
  useLayoutEffect(() => {
    // Restore any previously elevated elements first
    elevatedElsRef.current.forEach(({ el, zIndex, position }) => {
      el.style.zIndex = zIndex;
      el.style.position = position;
    });
    elevatedElsRef.current = [];

    if (!currentTargetElement || !isOpen) {
      // Also restore last target highlight if present
      if (lastTargetRef.current) {
        lastTargetRef.current.style.boxShadow = lastTargetBoxShadowRef.current;
        lastTargetRef.current = null;
        lastTargetBoxShadowRef.current = '';
      }
      return;
    }

    const highlightEl = getTourHighlightElement(currentTargetElement);

    // Helper to elevate an element above the tour backdrop (9998) but below the tooltip (12000)
    const elevate = (el: HTMLElement) => {
      const prev = { el, zIndex: el.style.zIndex, position: el.style.position };
      const computed = window.getComputedStyle(el);
      if (computed.position === 'static') {
        el.style.position = 'relative';
      }
      // Keep below tooltip (12000) but above overlay (9998)
      el.style.zIndex = '11000';
      elevatedElsRef.current.push(prev);
    };

    // Elevate the highlight surface (visible control, not e.g. MUI Switch's hidden input)
    elevate(highlightEl);
    // Add a visual highlight
    const originalBoxShadow = highlightEl.style.boxShadow;
    highlightEl.style.boxShadow = '0 0 20px 5px rgba(255, 255, 255, 0.7)';
    lastTargetRef.current = highlightEl;
    lastTargetBoxShadowRef.current = originalBoxShadow;

    // Walk up ancestors and elevate any that create a stacking context (z-index/transform/etc.)
    let parent: HTMLElement | null = highlightEl.parentElement;
    while (parent && parent !== document.body) {
      const cs = window.getComputedStyle(parent);
      const createsStackingContext =
        cs.zIndex !== 'auto' ||
        cs.transform !== 'none' ||
        cs.perspective !== 'none' ||
        cs.filter !== 'none' ||
        cs.mixBlendMode !== 'normal' ||
        cs.position !== 'static' ||
        cs.willChange.includes('transform') ||
        cs.willChange.includes('position');
      if (createsStackingContext) {
        elevate(parent);
      }
      parent = parent.parentElement;
    }

    // Clean up: restore everything we changed
    return () => {
      highlightEl.style.boxShadow = originalBoxShadow;
      elevatedElsRef.current.forEach(({ el, zIndex, position }) => {
        el.style.zIndex = zIndex;
        el.style.position = position;
      });
      elevatedElsRef.current = [];
    };
  }, [currentTargetElement, isOpen]);

  // When the tour closes, ensure any focus and elevated styles are removed
  useEffect(() => {
    if (!isOpen) {
      // Cancel any in-flight step transition (covers external close that
      // bypasses handleClose).
      if (navRafRef.current !== null) {
        cancelAnimationFrame(navRafRef.current);
        navRafRef.current = null;
      }
      pendingStepRef.current = null;
      // If focused element is inside the last target, blur it
      const active = document.activeElement as HTMLElement | null;
      if (active && lastTargetRef.current && lastTargetRef.current.contains(active)) {
        try {
          active.blur();
        } catch {
          // intentionally empty
        }
      }
      // Restore any elevated elements
      elevatedElsRef.current.forEach(({ el, zIndex, position }) => {
        el.style.zIndex = zIndex;
        el.style.position = position;
      });
      elevatedElsRef.current = [];
      // Restore last target highlight, if any
      if (lastTargetRef.current) {
        lastTargetRef.current.style.boxShadow = lastTargetBoxShadowRef.current;
        lastTargetRef.current = null;
        lastTargetBoxShadowRef.current = '';
      }
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && preTourSetup) {
      preTourSetup();
    }
  }, [isOpen]);

  const handleClose = () => {
    // Cancel any in-flight step transition so it can't fire after we close.
    if (navRafRef.current !== null) {
      cancelAnimationFrame(navRafRef.current);
      navRafRef.current = null;
    }
    pendingStepRef.current = null;
    if (currentStepObj?.postStepFunction) {
      currentStepObj.postStepFunction();
    }
    setCurrentStep(0);
    // Proactively blur if target or its child is focused
    const active = document.activeElement as HTMLElement | null;
    const hitTarget = currentTargetElement ? getTourHighlightElement(currentTargetElement) : null;
    if (active && hitTarget && hitTarget.contains(active)) {
      try {
        active.blur();
      } catch {
        // intentionally empty
      }
    }
    if (currentStepObj.onClose) {
      currentStepObj.onClose();
    }
    if (onClose) onClose();
  };

  // Handle clicks outside the tooltip to close the tour
  useEffect(() => {
    if (!isOpen) return;

    const handleOutsideClick = (e: MouseEvent) => {
      // Skip if we're still in the intro
      if (showIntro) return;

      // Check if the click is on the tooltip or its children
      const isClickOnTooltip = tooltipRef.current?.contains(e.target as Node);
      // Include visible control bounds (e.g. MUI Switch thumb/track, not only the hidden input)
      const hitTarget = currentTargetElement ? getTourHighlightElement(currentTargetElement) : null;
      const isClickOnTarget = hitTarget?.contains(e.target as Node);
      // Check if click is on the close button (which has its own handler)
      const closeButton = document.querySelector('[data-close-tour="true"]');
      const isClickOnCloseButton = closeButton?.contains(e.target as Node);

      // If the click is outside tooltip and target, close the tour
      if (!isClickOnTooltip && !isClickOnTarget && !isClickOnCloseButton) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [isOpen, showIntro, currentTargetElement, currentStepObj]);

  // Fix position name typo in enum
  useEffect(() => {
    if (prevStepRef.current !== currentStep) {
      prevStepRef.current = currentStep;
    }
  }, [currentStep]);

  if (!isOpen) return null;

  // Show intro modal before the tour starts
  if (showIntro) {
    return (
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          zIndex: 13000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'rgba(10, 20, 40, 0.85)',
        }}
      >
        <Paper
          elevation={4}
          sx={{
            p: 4,
            borderRadius: 3,
            minWidth: 340,
            maxWidth: '90vw',
            textAlign: 'center',
            bgcolor: 'rgb(5, 11, 19)',
            color: 'white',
          }}
        >
          <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
            Welcome
          </Typography>
          <Typography sx={{ mb: 3 }}>
            Would you like a quick walkthrough of the main features?
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowIntro(false)}
              sx={{ fontWeight: 700 }}
            >
              Start Tour
            </Button>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setShowIntro(false);
                if (onSkipTour) onSkipTour();
                handleClose();
              }}
              sx={{ fontWeight: 700, color: 'white', borderColor: 'white' }}
            >
              Skip
            </Button>
          </Box>
          {videoLink && (
            <Box
              sx={{
                mt: 2,
                display: 'flex',
                justifyContent: 'center',
                // backgroundColor: 'blue'
              }}
            >
              <Button
                href={videoLink}
                target="_blank"
                rel="noopener noreferrer"
                variant="text"
                color="primary"
                startIcon={<PlayCircle size={18} />}
                sx={{ fontWeight: 700, color: 'white', textDecoration: 'underline' }}
              >
                Watch Video
              </Button>
            </Box>
          )}
        </Paper>
      </Box>
    );
  }

  if (!currentStepObj) return null;

  // The arrow's ::before is a square rotated 45deg. A box-shadow in local (pre-rotation)
  // space gets rotated too, so we compute the local offset that produces a screen-space
  // shadow pointing away from the tooltip body for each placement.
  const arrowBoxShadow: Record<string, string> = {
    bottom: '-1px -1px 0 rgba(255, 255, 255, 0.1)',
    top: '1px 1px 0 rgba(255, 255, 255, 0.1)',
    right: '-1px 1px 0 rgba(255, 255, 255, 0.1)',
    left: '1px -1px 0 rgba(255, 255, 255, 0.1)',
  };

  return (
    <>
      <Box
        sx={{
          top: 0,
          left: 0,
          position: 'fixed',
          zIndex: 9998,
          height: '100vh',
          width: '100vw',
          opacity: 0.7,
          background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
        }}
      />
      <Box
        sx={{
          position: 'fixed',
          zIndex: 9998,
          height: '100vh',
          width: '100vw',
          opacity: 0.7,
          background: 'linear-gradient(229.67deg, #1D2F44 -6.17%, #071423 89.04%)',
        }}
      />
      <Box
        onClick={handleClose}
        {...{ [APP_TOUR_CHROME_ATTR]: '' }}
        sx={{
          position: 'fixed',
          top: 20,
          left: 20,
          zIndex: 12000,
          cursor: 'pointer',
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: 'rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
          pointerEvents: 'auto',
        }}
      >
        ✕
      </Box>
      <Box sx={{ position: 'fixed', inset: 0, zIndex: 11000, pointerEvents: 'none' }}>
        <Tooltip
          open={true}
          placement={currentPosition as 'left' | 'right' | 'top' | 'bottom'}
          PopperProps={{
            className: APP_TOUR_POPPER_CLASS,
            anchorEl: tooltipAnchorEl,
            modifiers: popperModifiers,
            // Until the step's anchor resolves AND the popper has positioned
            // itself over it, Popper paints at the viewport origin (0,0) and
            // then snaps to the target. Keep it transparent and non-interactive
            // until both are true so that origin flash never shows (MUS-275).
            sx: {
              zIndex: 12000,
              opacity: tooltipVisible ? 1 : 0,
              pointerEvents: tooltipVisible ? undefined : 'none',
            },
          }}
          componentsProps={{
            tooltip: {
              sx: {
                margin: '10px',
                bgcolor: bgColor,
                color: 'white',
                borderRadius: 2,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.25)',
                p: 2,
                maxWidth: 'none',
                width: 350,
                border: '1px solid rgba(255, 255, 255, 0.1)',
                zIndex: 12000,
              },
            },
            arrow: {
              sx: {
                zIndex: 12000,
                color: bgColor,
                fontSize: '1.5rem',
                '&::before': {
                  boxShadow: arrowBoxShadow[currentPosition] ?? 'none',
                },
              },
            },
          }}
          title={
            <Box ref={tooltipRef}>
              <Box sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>
                    {steps[currentStep].title}
                  </Box>
                  {currentStepObj.newFeature && (
                    <Box
                      sx={{
                        fontSize: 12,
                        opacity: 0.7,
                        mb: 1,
                        transform: 'rotate(-20deg) translateY(-10px)',
                        color: 'lightgreen',
                      }}
                    >
                      NEW!
                    </Box>
                  )}
                </Box>
                <Box
                  sx={{
                    fontSize: 15,
                    opacity: 1,
                    lineHeight: 1.7,
                    fontWeight: 400,
                    letterSpacing: '0.015em',
                  }}
                >
                  {steps[currentStep].content}
                </Box>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mt: 2,
                }}
              >
                {steps.length > 1 ? (
                  <Box sx={{ fontSize: 12, opacity: 0.7 }}>
                    {currentStep + 1} of {steps.length}
                  </Box>
                ) : (
                  <Box />
                )}

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  {currentStep > 0 && (
                    <Box
                      onClick={goToPrevStep}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        bgcolor: 'rgba(255, 255, 255, 0.1)',
                        color: 'white',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' },
                        pointerEvents: 'auto',
                      }}
                    >
                      <ChevronLeft size={18} />
                    </Box>
                  )}

                  <Box
                    onClick={goToNextStep}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      px: 1.5,
                      height: 28,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: 13,
                      '&:hover': { bgcolor: 'primary.dark' },
                      pointerEvents: 'auto',
                    }}
                  >
                    {currentStep < steps.length - 1
                      ? 'NEXT'
                      : steps.every((s) => s.newFeature)
                        ? 'GREAT'
                        : 'FINISH'}
                  </Box>
                </Box>
              </Box>
            </Box>
          }
          arrow
        >
          <span style={{ display: 'inline-block' }}></span>
        </Tooltip>
      </Box>
    </>
  );
}
